import type { AuthSession } from './AuthContext';
import { AUTH_SESSION_STORAGE_KEY } from './AuthContext';
import { apiFetch } from '../common/api/apiFetchClient';
import { buildApiUrl } from '../common/api/apiBaseUrl';

const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
const CHECK_INTERVAL_MS = 30 * 1000; // 30 seconds
const SESSION_MAX_AGE_MS = 60 * 60 * 1000; // 60-minute hard session limit
const SESSION_WARN_AGE_MS = 57 * 60 * 1000; // warn 3 minutes before the hard limit
const REFRESH_MAX_RETRIES = 2; // additional attempts after the first try (3 total)
const REFRESH_RETRY_DELAY_MS = 3 * 1000; // 3-second back-off between retries

export const SESSION_STARTED_AT_KEY = 'rfi.auth.sessionStartedAt';

function getTokenExpiry(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1])) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

async function callRefresh(token: string, authType: AuthSession['authType']): Promise<string | null> {
  const refreshPath = authType === 'EXTERNAL'
    ? '/api/rfi/auth/external/refresh'
    : '/api/rfi/auth/internal/refresh';

  for (let attempt = 1; attempt <= REFRESH_MAX_RETRIES + 1; attempt++) {
    if (attempt > 1) {
      await new Promise((resolve) => {return setTimeout(resolve, REFRESH_RETRY_DELAY_MS)});
    }

    try {
      const data = await apiFetch<{ access_token?: string }>(buildApiUrl(refreshPath), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (data.access_token) {
        return data.access_token;
      }
    } catch {
      // continue to next retry
    }
  }

  return null;
}

/**
 * Starts the token lifecycle manager.
 *
 * Every 30 seconds:
 * 1. At 57 minutes: calls onSessionWarning() once — gives the user a 3-minute window to save work.
 * 2. At 60 minutes: calls onHardTimeout() which immediately clears the session.
 * 3. Checks whether the token is within 5 minutes of expiry → silently refreshes via /internal/refresh
 * 4. If refresh fails → calls onRefreshFailed() (warning dialog only — hard-timeout path handles logout).
 *
 * Returns a cleanup function — call it to stop the timer (e.g. on logout).
 */
export function startTokenManager(
  getSession: () => AuthSession | null,
  updateToken: (newToken: string) => void,
  onRefreshFailed: () => void,
  onSessionWarning: () => void,
  onHardTimeout: () => void,
): () => void {
  let warnFired = false;

  const intervalId = setInterval(async () => {
    const session = getSession();
    if (!session?.token) return;

    const sessionStartedAt = typeof window !== 'undefined'
      ? parseInt(window.sessionStorage.getItem(SESSION_STARTED_AT_KEY) ?? '0', 10)
      : 0;

    const sessionAgeMs = sessionStartedAt > 0 ? Date.now() - sessionStartedAt : 0;

    // Hard session timeout at 60 minutes — immediate forced logout
    if (sessionStartedAt > 0 && sessionAgeMs >= SESSION_MAX_AGE_MS) {
      clearInterval(intervalId);
      onHardTimeout();
      return;
    }

    // 3-minute advance warning: fires once at the 57-minute mark
    if (sessionStartedAt > 0 && !warnFired && sessionAgeMs >= SESSION_WARN_AGE_MS) {
      warnFired = true;
      onSessionWarning();
    }

    // Soft token refresh: refresh within 5 minutes of expiry
    const expiresAt = getTokenExpiry(session.token);
    if (expiresAt === null) return;

    const msRemaining = expiresAt - Date.now();
    if (msRemaining > TOKEN_REFRESH_THRESHOLD_MS) return;

    const newToken = await callRefresh(session.token, session.authType);

    if (!newToken) {
      // Do not clear the interval — hard timeout at 60 min remains the sole forced-logout trigger.
      onRefreshFailed();
      return;
    }

    // Update sessionStorage and app state with the new token
    const updatedSession: AuthSession = { ...session, token: newToken };
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(updatedSession));
    }
    updateToken(newToken);
  }, CHECK_INTERVAL_MS);

  return () => {return clearInterval(intervalId)};
}
